import {useCallback, useEffect, useState} from 'react';

import {getUsers} from 'src/openapi/admin/apis/UserIdentityApi';
import {getAssignUsers} from 'src/openapi/admin/apis/VendorManagementApi';
import {useAppDispatch} from 'src/store';

import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {User} from 'src/openapi/admin/models/User';

export type VendorUser = User & {
    jobTitle?: string | null,
    phoneNumber?: string | null,
};

type Return = {
    users: VendorUser[],
    loading: boolean,
    error: ApiError | null,
    reload: () => void,
    removeUserFromState: (userId: number) => void,
};

export const useVendorUsers = (vendorId?: string): Return => {
    const dispatch = useAppDispatch();
    const sortUsers = useCallback((list: VendorUser[]) => (
        [...list].sort((a, b) => (a.email ?? '').localeCompare(b.email ?? '', undefined, {sensitivity: 'base'}))
    ), []);
    const [users, setUsers] = useState<VendorUser[]>([]);
    const [loading, setLoading] = useState(Boolean(vendorId));
    const [error, setError] = useState<ApiError | null>(null);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (!vendorId) {
            setUsers([]);
            setLoading(false);
            setError(null);
            return;
        }

        const controller = new AbortController();
        let isActive = true;

        const loadUsers = async () => {
            setLoading(true);
            setError(null);
            try {
                const idResponse = await dispatch(getAssignUsers({vendorId}, {signal: controller.signal}));
                if (!isActive) {
                    return;
                }
                if (!idResponse.payload?.success) {
                    setUsers([]);
                    setError((idResponse.payload?.data as ApiError) ?? null);
                    return;
                }

                const userIds = (idResponse.payload.data as number[]) ?? [];

                if (userIds.length === 0) {
                    setUsers([]);
                    setError(null);
                    return;
                }

                const usersResponse = await dispatch(getUsers({requestBody: userIds}, {signal: controller.signal}));
                if (!isActive) {
                    return;
                }

                if (usersResponse.payload?.success) {
                    const fetchedUsers = (usersResponse.payload.data ?? []) as VendorUser[];
                    const userMap = new Map<number, VendorUser>(
                        fetchedUsers.map(user => [user.userId, user]),
                    );
                    const orderedUsers = userIds
                        .map(userId => userMap.get(userId))
                        .filter(Boolean) as VendorUser[];

                    setUsers(sortUsers(orderedUsers));
                    setError(null);
                } else {
                    setUsers([]);
                    setError((usersResponse.payload?.data as ApiError) ?? null);
                }
            } catch (err) {
                if ((err as {name?: string})?.name === 'AbortError' || !isActive) {
                    return;
                }
                setError((err as ApiError) ?? null);
                setUsers([]);
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };

        loadUsers();

        return () => {
            isActive = false;
            controller.abort();
        };
    }, [dispatch, sortUsers, vendorId, reloadKey]);

    const reload = useCallback(() => {
        setReloadKey(prev => prev + 1);
    }, []);

    const removeUserFromState = useCallback((userId: number) => {
        setUsers(prev => sortUsers(prev.filter(user => user.userId !== userId)));
    }, [sortUsers]);

    return {
        users,
        loading,
        error,
        reload,
        removeUserFromState,
    };
};
